import type { Rules, Variant } from "@app/protocol";
import { type RuleOption, ruleOptionsFor } from "@app/rules";
import { useT } from "../i18n/context";
import { Choice, Panel } from "./Choices";

/** Translation key of a rule or one of its values; every game's rules live under `rule.<game>.<key>`. */
export function ruleLabelKey(variant: Variant, key: string, value?: boolean | number): string {
  return value === undefined ? `rule.${variant}.${key}` : `rule.${variant}.${key}.${String(value)}`;
}

function RulePanel({
  variant,
  option,
  value,
  onChange,
}: {
  variant: Variant;
  option: RuleOption;
  value: boolean | number;
  onChange: (value: boolean | number) => void;
}) {
  const { t } = useT();
  const values: readonly (boolean | number)[] =
    option.kind === "toggle" ? [true, false] : option.choices;
  return (
    <Panel label={t(ruleLabelKey(variant, option.key) as "home.goal")}>
      {values.map((v) => (
        <Choice
          key={String(v)}
          label={t(ruleLabelKey(variant, option.key, v) as "home.goal")}
          selected={value === v}
          onClick={() => onChange(v)}
        />
      ))}
    </Panel>
  );
}

/** The game's house rules, one panel each; nothing renders for a game without options. */
export function HouseRules({
  variant,
  rules,
  onChange,
}: {
  variant: Variant;
  rules: Rules;
  onChange: (rules: Rules) => void;
}) {
  const options = ruleOptionsFor(variant);
  if (options.length === 0) return null;
  return (
    <>
      {options.map((option) => (
        <RulePanel
          key={option.key}
          variant={variant}
          option={option}
          value={rules[option.key] ?? option.default}
          onChange={(value) => onChange({ ...rules, [option.key]: value })}
        />
      ))}
    </>
  );
}
